import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CyclingEvent } from '@radevents/shared';
import { useAuth } from '../contexts/AuthContext';
import eventService from '../services/eventService';
import { rememberPendingFavorite } from '../services/pendingFavorite';
import { clearListCache } from './useListCache';
import { umamiTrack } from '../services/analytics';

/**
 * Vormerken aus einer Event-Liste außerhalb der Startseite (Veranstalterseite, seit 30.10.2026).
 *
 * Dieselben Schritte wie `Landing.handleFavorite`: ohne Anmeldung den Klick parken und zur
 * Anmeldung, sonst umschalten, zählen und die Listen-Caches verwerfen, damit Startseite und
 * Meine Events den neuen Stand neu holen. Die Liste selbst aktualisiert der Aufrufer über `onChanged`.
 */
export function useEventMerken(onChanged: (id: number, favorited: boolean) => void) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(async (event: CyclingEvent) => {
    if (!isAuthenticated) {
      rememberPendingFavorite(event.id);
      navigate('/login', { state: { from: { pathname: location.pathname } } });
      return;
    }
    try {
      if (event.favorited) await eventService.removeFavorite(event.id);
      else await eventService.addFavorite(event.id);
      umamiTrack('event-favorit', { id: event.id, titel: event.title, aktion: event.favorited ? 'entfernt' : 'hinzugefuegt' });
      onChanged(event.id, !event.favorited);
      clearListCache('landing');
      clearListCache('favorites');
    } catch {
      toast.error('Vormerken fehlgeschlagen');
    }
  }, [isAuthenticated, navigate, location.pathname, onChanged]);
}
